// Glass-cockpit Engine/Warning Display (EWD).
//
// HONESTY NOTE: ADS-B telemetry does NOT include engine parameters (N1, EGT,
// fuel flow, etc.). Everything engine-related in this component is an ESTIMATE
// derived purely from flight phase (velocity / altitude / vertical rate). It is
// labelled "(EST)" throughout and must never be read as real engine data.

import { metresToFeet, msToKnots, prettyCallsign } from "../lib/format.js";
import "./EngineDisplay.css";

// m/s -> feet per minute (matches format.js internal constant).
const msToFpm = (ms) => (ms == null ? null : Math.round(ms * 196.85));

// Derive the coarse flight phase and an estimated N1 (% of max fan speed).
function derivePhase(aircraft) {
  const { onGround, verticalRate, baroAltitude } = aircraft;
  const vr = verticalRate ?? 0;
  const altFt = metresToFeet(baroAltitude) ?? 0;

  if (onGround) return { phase: "TAXI/GND", n1: 25 };
  if (vr > 2.5) return { phase: "CLIMB", n1: 92 };
  if (vr < -2.5) return { phase: "DESCENT", n1: 48 };
  if (altFt > 25000) return { phase: "CRUISE", n1: 84 };
  return { phase: "LEVEL", n1: 70 };
}

// Engine count heuristic from the aircraft type designator.
function engineCount(type) {
  if (!type) return 2;
  const t = String(type).toUpperCase();
  if (t.startsWith("A388") || t.startsWith("B74")) return 4;
  if (t.includes("380") || t.includes("747")) return 4;
  return 2;
}

// Pick a status colour band for an N1 value.
function n1Colour(n1) {
  if (n1 >= 90) return "var(--ewd-amber)";
  return "var(--ewd-green)";
}

// A single circular arc N1 gauge with a needle.
function N1Gauge({ index, n1 }) {
  // Gauge sweeps a 270° arc from 135° (bottom-left) clockwise to 45°.
  const START = 135;
  const SWEEP = 270;
  const clamped = Math.max(0, Math.min(100, n1));
  const size = 120;
  const cx = size / 2;
  const cy = size / 2;
  const r = 46;

  // Arc path helper.
  const polar = (angleDeg, radius = r) => {
    const a = (angleDeg * Math.PI) / 180;
    return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)];
  };
  const arcPath = (fromPct, toPct) => {
    const a0 = START + (SWEEP * fromPct) / 100;
    const a1 = START + (SWEEP * toPct) / 100;
    const [x0, y0] = polar(a0);
    const [x1, y1] = polar(a1);
    const large = a1 - a0 > 180 ? 1 : 0;
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
  };

  const needleAngle = START + (SWEEP * clamped) / 100;
  const [nx, ny] = polar(needleAngle, r - 6);
  const colour = n1Colour(clamped);
  const label = `Engine ${index + 1} estimated N1 ${Math.round(clamped)} percent`;

  return (
    <div className="ewd-gauge" role="img" aria-label={label}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="ewd-gauge-svg"
        aria-hidden="true"
      >
        {/* Track */}
        <path d={arcPath(0, 100)} className="ewd-gauge-track" />
        {/* Amber upper band */}
        <path d={arcPath(90, 100)} className="ewd-gauge-band-amber" />
        {/* Active fill up to current N1 */}
        <path
          d={arcPath(0, clamped)}
          className="ewd-gauge-fill"
          style={{ stroke: colour }}
        />
        {/* Needle */}
        <line
          x1={cx}
          y1={cy}
          x2={nx}
          y2={ny}
          className="ewd-gauge-needle"
        />
        <circle cx={cx} cy={cy} r="4" className="ewd-gauge-hub" />
      </svg>
      <div className="ewd-gauge-readout" style={{ color: colour }}>
        {Math.round(clamped)}
        <span className="ewd-gauge-unit">%</span>
      </div>
      <div className="ewd-gauge-caption">
        ENG {index + 1} · N1 (EST)
      </div>
    </div>
  );
}

// A secondary EGT-style bar, scaled from N1 (also estimated).
function EgtBar({ index, n1 }) {
  // Map N1 (0-100) to a plausible EGT-looking range (~300–850 "°C").
  const egt = Math.round(300 + (n1 / 100) * 550);
  const pct = Math.max(0, Math.min(100, ((egt - 300) / 550) * 100));
  const hot = egt > 780;
  const colour = hot ? "var(--ewd-amber)" : "var(--ewd-green)";
  const label = `Engine ${index + 1} estimated EGT ${egt} degrees`;

  return (
    <div className="ewd-egt-row" role="img" aria-label={label}>
      <span className="ewd-egt-tag">EGT {index + 1} (EST)</span>
      <div className="ewd-egt-track">
        <div
          className="ewd-egt-fill"
          style={{ width: `${pct}%`, background: colour }}
        />
      </div>
      <span className="ewd-egt-value" style={{ color: colour }}>
        {egt}°
      </span>
    </div>
  );
}

export default function EngineDisplay({ aircraft }) {
  if (!aircraft) {
    return (
      <section className="ewd-panel ewd-standby" aria-label="Engine Warning Display standby">
        <div className="ewd-standby-text">EWD STBY — SELECT AIRCRAFT</div>
      </section>
    );
  }

  const { phase, n1 } = derivePhase(aircraft);
  const engines = Array.from({ length: engineCount(aircraft.type) }, (_, i) => i);

  const altFt = metresToFeet(aircraft.baroAltitude);
  const spdKn = msToKnots(aircraft.velocity);
  const vsFpm = msToFpm(aircraft.verticalRate);
  const hdg = aircraft.heading == null ? null : Math.round(aircraft.heading);
  const squawk = aircraft.squawk ?? "----";
  const callsign = prettyCallsign(aircraft.callsign);

  const vsArrow = vsFpm == null || Math.abs(vsFpm) < 60 ? "" : vsFpm > 0 ? "▲" : "▼";
  const vsText =
    vsFpm == null ? "----" : `${vsArrow} ${Math.abs(vsFpm).toLocaleString()}`;

  return (
    <section className="ewd-panel" aria-label={`Engine Warning Display for ${callsign}`}>
      <header className="ewd-header">
        <span className="ewd-title">EWD</span>
        <span className="ewd-callsign">{callsign}</span>
        <span className="ewd-phase-chip">{phase}</span>
      </header>

      {/* Primary N1 gauges */}
      <div className="ewd-gauges">
        {engines.map((i) => (
          <N1Gauge key={i} index={i} n1={n1} />
        ))}
      </div>

      {/* Secondary EGT bars */}
      <div className="ewd-egt">
        {engines.map((i) => (
          <EgtBar key={i} index={i} n1={n1} />
        ))}
      </div>

      {/* Memo / derived readouts */}
      <div className="ewd-memo" aria-label="Derived flight readouts">
        <div className="ewd-memo-grid">
          <span className="ewd-memo-key">FLIGHT PHASE</span>
          <span className="ewd-memo-val">{phase}</span>

          <span className="ewd-memo-key">ALT</span>
          <span className="ewd-memo-val">
            {altFt == null ? "----" : altFt.toLocaleString()} <em>ft</em>
          </span>

          <span className="ewd-memo-key">SPD</span>
          <span className="ewd-memo-val">
            {spdKn == null ? "----" : spdKn} <em>kn</em>
          </span>

          <span className="ewd-memo-key">V/S</span>
          <span className="ewd-memo-val">
            {vsText} <em>fpm</em>
          </span>

          <span className="ewd-memo-key">HDG</span>
          <span className="ewd-memo-val">
            {hdg == null ? "---" : String(hdg).padStart(3, "0")}<em>°</em>
          </span>

          <span className="ewd-memo-key">SQUAWK</span>
          <span className="ewd-memo-val">{squawk}</span>
        </div>
        <p className="ewd-memo-note">
          Engine values estimated — not transmitted by ADS-B
        </p>
      </div>
    </section>
  );
}
